/**
 * Application state management
 */
import { CONFIG } from '../config/config.js';

export const AppState = {
  // Animation state
  isPlaying: false,
  animationFrameId: null,
  lastTimestamp: 0,
  sigmaValue: CONFIG.defaultSigma,
  cellSize: CONFIG.defaultCellSize,
  isPaused: false,
  pauseStartTime: 0,
  lastAdjustedSlider: null,
  previousOpacities: {},
  
  // Map state
  mapCenter: [-10983947, 4611158], // Center of continental US
  mapZoom: 5, // Zoom level to show most of continental US
  
  // Layer visibility state
  showPoints: true,
  showBoundary: false,
  
  // Reference layer state (only one can be active)
  referenceLayer: 'none', // 'none', 'cz20', 'cbsa', 'fua', 'efua', 'cmaca'
  
  // Basemap state
  basemap: 'carto', // 'osm', 'carto', 'esriImagery', 'esriStreetMap', 'esriTopo', 'esriRelief'
  
  // Filter state
  populationFilter: 0,
  populationScale: 'linear', // 'linear' or 'logarithmic'
  
  // Animation state parameters for sharing
  animationSpeed: CONFIG.animation.sigmasPerSecond,
  
  // UI state - which panels are expanded
  expandedPanels: {
    sigmaSlider: true,
    watershedLayers: true,
    population: false,
    referenceLayers: false,
    basemaps: false
  },
  
  /**
   * Initialize the application state
   */
  initialize() {
    // Initialize previousOpacities with default values
    CONFIG.sigmaValues.forEach(sigma => {
      this.previousOpacities[sigma] = sigma === CONFIG.defaultSigma ? 1.0 : 0;
    });
  },
  
  /**
   * Get all sigma values with enabled toggles
   */
  getEnabledSigmaValues() {
    return CONFIG.sigmaValues.filter(sigma => 
      document.getElementById(`sigma-${sigma}-toggle`).checked
    );
  },
  
  /**
   * Get the currently active sigma value based on opacities
   */
  getActiveSigma() {
    // Get current opacity values from the sliders
    const sigmaOpacities = {};
    CONFIG.sigmaValues.forEach(sigma => {
      sigmaOpacities[sigma] = parseInt(document.getElementById(`opacity-${sigma}`).value, 10) / 100;
    });
    
    // Check which layer has opacity > 0.5
    for (const sigma of CONFIG.sigmaValues) {
      if (sigmaOpacities[sigma] > 0.5) return sigma;
    }
    
    // If no layer has opacity > 0.5, return the sigma with highest opacity
    let maxOpacitySigma = CONFIG.sigmaValues[0];
    let maxOpacity = sigmaOpacities[maxOpacitySigma];
    
    for (let i = 1; i < CONFIG.sigmaValues.length; i++) {
      const sigma = CONFIG.sigmaValues[i];
      if (sigmaOpacities[sigma] > maxOpacity) {
        maxOpacity = sigmaOpacities[sigma];
        maxOpacitySigma = sigma;
      }
    }
    
    return maxOpacitySigma;
  },
  
  /**
   * Captures the current state of the application
   * @returns {Object} The current state object
   */
  captureCurrentState() {
    // Update map center and zoom from the current map view
    if (window.mapController && window.mapController.map) {
      const view = window.mapController.map.getView();
      this.mapCenter = view.getCenter();
      this.mapZoom = view.getZoom();
    }
    
    // Update layer visibility from UI controls
    this.showPoints = document.getElementById('points-toggle').checked;
    this.showBoundary = document.getElementById('boundary-toggle').checked;
    
    // Update reference layer selection
    const referenceRadios = document.querySelectorAll('input[name="reference-layer"]');
    for (const radio of referenceRadios) {
      if (radio.checked) {
        if (radio.id === 'no-reference') this.referenceLayer = 'none';
        else if (radio.id === 'cz20-toggle') this.referenceLayer = 'cz20';
        else if (radio.id === 'cbsa-toggle') this.referenceLayer = 'cbsa';
        else if (radio.id === 'fua-toggle') this.referenceLayer = 'fua';
        else if (radio.id === 'efua-toggle') this.referenceLayer = 'efua';
        else if (radio.id === 'pl-all-uscbsas-toggle') this.referenceLayer = 'pl_all_uscbsas';
        break;
      }
    }
    
    // Update basemap selection
    const basemapRadios = document.querySelectorAll('input[name="basemap"]');
    for (const radio of basemapRadios) {
      if (radio.checked) {
        if (radio.id === 'osm-basemap') this.basemap = 'osm';
        else if (radio.id === 'carto-basemap') this.basemap = 'carto';
        else if (radio.id === 'esri-imagery') this.basemap = 'esriImagery';
        else if (radio.id === 'esri-streetmap') this.basemap = 'esriStreetMap';
        else if (radio.id === 'esri-topo') this.basemap = 'esriTopo';
        else if (radio.id === 'esri-relief') this.basemap = 'esriRelief';
        break;
      }
    }
    
    // Update POI filter and scale type
    const logScale = document.getElementById('log-scale').checked;
    this.populationScale = logScale ? 'logarithmic' : 'linear';
    
    // Get POI filter value from the appropriate slider based on scale type
    if (logScale) {
      const poiLogSlider = document.getElementById('poi-slider-log');
      if (poiLogSlider) {
        // Convert logarithmic value to linear value for storage and sharing
        const logValue = parseFloat(poiLogSlider.value);
        if (logValue === 0) {
          this.populationFilter = 0;
        } else {
          this.populationFilter = Math.round(Math.pow(10, logValue) - 1);
        }
      }
    } else {
      const poiSlider = document.getElementById('poi-slider');
      if (poiSlider) {
        this.populationFilter = parseInt(poiSlider.value, 10);
      }
    }
    
    // Update which panels are expanded
    this.expandedPanels = {
      sigmaSlider: document.querySelector('#sigma-slider-header')?.classList.contains('active') || false,
      watershedLayers: document.querySelector('#watershed-layers-header')?.classList.contains('active') || false,
      population: document.querySelector('#poi-header')?.classList.contains('active') || false,
      referenceLayers: document.querySelector('#reference-layers-header')?.classList.contains('active') || false,
      basemaps: document.querySelector('#basemaps-header')?.classList.contains('active') || false
    };
    
    // Save the current active sigma and cell size
    this.sigmaValue = this.getActiveSigma();
    
    // Determine cell size from radio buttons
    if (document.getElementById('cell-size-100') && document.getElementById('cell-size-100').checked) {
      this.cellSize = 100;
    } else if (document.getElementById('cell-size-1000') && document.getElementById('cell-size-1000').checked) {
      this.cellSize = 1000;
    }
    // Don't change this.cellSize if neither radio is checked - keep the existing value

    // Update animation speed from playback speed selector
    const playbackSpeedSelect = document.getElementById('playback-speed');
    if (playbackSpeedSelect) {
      this.animationSpeed = parseFloat(playbackSpeedSelect.value);
    }
    
    // Return a copy of the state for serialization
    return {
      mapCenter: this.mapCenter,
      mapZoom: this.mapZoom,
      sigmaValue: this.sigmaValue,
      cellSize: this.cellSize,
      showPoints: this.showPoints,
      showBoundary: this.showBoundary,
      referenceLayer: this.referenceLayer,
      basemap: this.basemap,
      populationFilter: this.populationFilter,
      expandedPanels: { ...this.expandedPanels }
    };
  },
  
  /**
   * Creates a shareable URL with the current state
   * @returns {string} URL with state parameters
   */
  createShareableUrl() {
    const state = this.captureCurrentState();
    const params = new URLSearchParams();
    
    // Add state parameters to URL
    params.set('center', state.mapCenter.join(','));
    params.set('zoom', state.mapZoom.toString());
    params.set('sigma', state.sigmaValue.toString());
    params.set('cellSize', state.cellSize.toString());
    params.set('points', state.showPoints.toString());
    params.set('boundary', state.showBoundary.toString());
    params.set('ref', state.referenceLayer);
    params.set('base', state.basemap);
    params.set('popMin', state.populationFilter.toString());
    params.set('popScale', this.populationScale);
    
    // Add animation state parameters
    params.set('isPlaying', this.isPlaying.toString());
    params.set('speed', this.animationSpeed.toString());
    
    // Encode expanded panels as a single parameter
    const panels = Object.entries(state.expandedPanels)
      .filter(([_, isExpanded]) => isExpanded)
      .map(([panel]) => panel)
      .join(',');
    
    if (panels) {
      params.set('panels', panels);
    }
    
    // Create URL with current location and parameters
    const url = new URL(window.location.href);
    url.search = params.toString();
    
    return url.href;
  },
  
  /**
   * Applies a state from URL parameters
   * @param {URLSearchParams} params - URL parameters
   */
  applyStateFromUrl(params) {
    if (!params) return;
    
    // Apply map center and zoom if provided
    if (params.has('center') && params.has('zoom') && window.mapController && window.mapController.map) {
      const center = params.get('center').split(',').map(parseFloat);
      const zoom = parseFloat(params.get('zoom'));
      
      if (center.length === 2 && !isNaN(center[0]) && !isNaN(center[1]) && !isNaN(zoom)) {
        window.mapController.map.getView().setCenter(center);
        window.mapController.map.getView().setZoom(zoom);
        this.mapCenter = center;
        this.mapZoom = zoom;
      }
    }
    
    // Apply sigma if provided
    if (params.has('sigma')) {
      const sigma = parseInt(params.get('sigma'), 10);
      if (!isNaN(sigma) && CONFIG.sigmaValues.includes(sigma)) {
        // Set the sigma slider to this value
        const sigmaSlider = document.getElementById('sigma-slider');
        if (sigmaSlider) {
          sigmaSlider.value = sigma;
          document.getElementById('sigma-display').textContent = sigma;
          this.sigmaValue = sigma;
          
          // Update layer opacities
          if (window.uiController) {
            window.uiController.updateLayersBySigma(sigma);
          }
        }
      }
    }
    
    // Apply layer visibility if provided
    if (params.has('points')) {
      const showPoints = params.get('points') === 'true';
      document.getElementById('points-toggle').checked = showPoints;
      this.showPoints = showPoints;
      
      // Apply the visibility change to the actual layer
      if (window.mapController && window.mapController.setLayerVisibility) {
        window.mapController.setLayerVisibility('points', showPoints);
      }
    }
    
    if (params.has('boundary')) {
      const showBoundary = params.get('boundary') === 'true';
      document.getElementById('boundary-toggle').checked = showBoundary;
      this.showBoundary = showBoundary;
      
      // Apply the visibility change to the actual layer
      if (window.mapController && window.mapController.setLayerVisibility) {
        window.mapController.setLayerVisibility('boundary', showBoundary);
      }
    }
    
    // Apply reference layer if provided
    if (params.has('ref')) {
      const refLayer = params.get('ref');
      
      // Find the corresponding radio button ID from config
      let refRadioId = 'no-reference'; // Default
      
      if (CONFIG.referenceLayers[refLayer]) {
        refRadioId = CONFIG.referenceLayers[refLayer].id;
      }
      
      // Check the radio button
      document.getElementById(refRadioId).checked = true;
      this.referenceLayer = refLayer;
      
      // Update the reference layer visibility
      if (window.mapController) {
        window.mapController.setReferenceLayerVisibility(refLayer);
      }
    }
    
    // Apply basemap if provided
    if (params.has('base')) {
      const basemap = params.get('base');
      let baseRadioId = 'osm-basemap'; // Default
      
      if (basemap === 'carto') baseRadioId = 'carto-basemap';
      else if (basemap === 'esriImagery') baseRadioId = 'esri-imagery';
      else if (basemap === 'esriStreetMap') baseRadioId = 'esri-streetmap';
      else if (basemap === 'esriTopo') baseRadioId = 'esri-topo';
      else if (basemap === 'esriRelief') baseRadioId = 'esri-relief';
      
      // Update radio button state
      const radioButton = document.getElementById(baseRadioId);
      if (radioButton) radioButton.checked = true;
      
      // Update the basemap state
      this.basemap = basemap;
      
      // Hide all basemaps first
      if (window.mapController && window.mapController.hideAllBasemaps) {
        window.mapController.hideAllBasemaps();
      }
      
      // Show only the selected basemap
      if (window.mapController && window.mapController.basemapLayers && window.mapController.basemapLayers[basemap]) {
        window.mapController.basemapLayers[basemap].setVisible(true);
      }
    }
    
    // Apply population filter if provided
    if (params.has('popMin')) {
      const popMin = parseInt(params.get('popMin'), 10);
      if (!isNaN(popMin)) {
        // Update this to use poi-slider instead of population-slider
        const poiSlider = document.getElementById('poi-slider');
        const poiLogSlider = document.getElementById('poi-slider-log');
        
        if (poiSlider) {
          poiSlider.value = popMin;
          this.populationFilter = popMin;
        }
        
        // Set population scale type if provided
        if (params.has('popScale')) {
          this.populationScale = params.get('popScale');
          
          // Update the UI to match the saved scale type
          const linearScale = document.getElementById('linear-scale');
          const logScale = document.getElementById('log-scale');
          
          if (this.populationScale === 'logarithmic') {
            if (logScale) logScale.checked = true;
            if (linearScale) linearScale.checked = false;
            if (poiSlider) poiSlider.style.display = 'none';
            
            if (poiLogSlider) {
              poiLogSlider.style.display = 'block';
              
              // Update log slider value to match linear value
              if (window.uiController && window.uiController.linearToLog) {
                poiLogSlider.value = window.uiController.linearToLog(popMin);
              } else {
                // Fallback if the controller method isn't available
                poiLogSlider.value = popMin === 0 ? 0 : Math.log10(popMin + 1);
              }
            }
          } else {
            // Default to linear scale
            if (linearScale) linearScale.checked = true;
            if (logScale) logScale.checked = false;
            if (poiSlider) poiSlider.style.display = 'block';
            if (poiLogSlider) poiLogSlider.style.display = 'none';
          }
        }
        
        // Apply the POI filter directly to the map layers
        if (window.mapController) {
          // Force update the POI filter value display
          const poiValue = document.getElementById('poi-value');
          if (poiValue) {
            poiValue.textContent = popMin.toString();
          }
          
          // Apply the filter to the visible layers
          if (window.mapController.updatePointsFilter) {
            window.mapController.updatePointsFilter(popMin);
          }
          if (window.mapController.updateBoundaryFilter) {
            window.mapController.updateBoundaryFilter(popMin);
          }
        }
      }
    }
    
    // Apply cell size if provided
    if (params.has('cellSize')) {
      const cellSize = parseInt(params.get('cellSize'), 10);
      if (!isNaN(cellSize) && CONFIG.cellSizes.includes(cellSize)) {
        this.cellSize = cellSize;
        
        // Update the cell size radio buttons
        if (cellSize === 100) {
          const radio100 = document.getElementById('cell-size-100');
          if (radio100) radio100.checked = true;
        } else if (cellSize === 1000) {
          const radio1000 = document.getElementById('cell-size-1000');
          if (radio1000) radio1000.checked = true;
        }
        
        // If map controller exists, update layers with new cell size
        if (window.mapController && window.mapController.updateCellSize) {
          window.mapController.updateCellSize(cellSize);
        }
      }
    }
    
    // Apply panel state if provided
    if (params.has('panels')) {
      const expandedPanelIds = params.get('panels').split(',');
      
      // Reset all panels to collapsed
      document.querySelectorAll('.collapsible-header').forEach(header => {
        header.classList.remove('active');
        const content = header.nextElementSibling;
        if (content) content.style.maxHeight = null;
      });
      
      // Expand the specified panels
      expandedPanelIds.forEach(panelId => {
        let headerId;
        
        if (panelId === 'sigmaSlider') headerId = 'sigma-slider-header';
        else if (panelId === 'watershedLayers') headerId = 'watershed-layers-header';
        else if (panelId === 'referenceLayers') headerId = 'reference-layers-header';
        else if (panelId === 'basemaps') headerId = 'basemaps-header';
        else if (panelId === 'presets') headerId = 'presets-header';
        
        if (headerId) {
          const header = document.getElementById(headerId);
          if (header) {
            header.classList.add('active');
            const content = header.nextElementSibling;
            if (content) content.style.maxHeight = content.scrollHeight + "px";
          }
        } else if (panelId === 'population') {
          const header = document.getElementById('poi-header');
          const content = document.getElementById('poi-content');
          if (header && content) {
            header.classList.add('active');
            content.classList.add('expanded');
          }
        }
      });
      
      // Update our internal state
      this.expandedPanels = {
        sigmaSlider: expandedPanelIds.includes('sigmaSlider'),
        watershedLayers: expandedPanelIds.includes('watershedLayers'),
        population: expandedPanelIds.includes('population'),
        referenceLayers: expandedPanelIds.includes('referenceLayers'),
        basemaps: expandedPanelIds.includes('basemaps'),
        presets: expandedPanelIds.includes('presets')
      };
    }
    
    // Apply animation state if provided
    if (params.has('isPlaying') && params.has('speed')) {
      const isPlaying = params.get('isPlaying') === 'true';
      const speed = parseFloat(params.get('speed'));
      
      if (!isNaN(speed) && speed > 0) {
        this.animationSpeed = speed;
        
        // Update speed input if it exists
        const speedInput = document.getElementById('animation-speed');
        if (speedInput) {
          speedInput.value = speed;
        }
        
        // Also update the playback-speed select element (used in the animateSigma function)
        const playbackSpeedSelect = document.getElementById('playback-speed');
        if (playbackSpeedSelect) {
          playbackSpeedSelect.value = speed;
        }
      }
      
      // Set the animation state
      this.isPlaying = isPlaying;
      
      // Start or stop the animation based on the parameter
      if (isPlaying && window.uiController) {
        // Start the animation if not already playing
        if (!this.animationFrameId) {
          window.uiController.startSigmaAnimation();
        }
      } else if (!isPlaying && this.animationFrameId && window.uiController) {
        // Stop the animation if it's playing
        window.uiController.stopSigmaAnimation();
      }
      
      // Update the play/pause button UI if it exists
      const playPauseBtn = document.getElementById('play-pause-btn');
      if (playPauseBtn) {
        if (isPlaying) {
          playPauseBtn.textContent = '\u23F8'; // Pause symbol
          playPauseBtn.setAttribute('title', 'Pause Animation');
        } else {
          playPauseBtn.textContent = '\u25B6'; // Play symbol
          playPauseBtn.setAttribute('title', 'Play Animation');
        }
      }
    }
    
    // Update all layer opacities based on the new state
    if (window.mapController) {
      window.mapController.updateAllLayersOpacity();
    }
  }
};

export default AppState;